import React, { useState, useRef } from "react";
import { useQuote } from "../context/QuoteContext";
import api from "../api";
import { uid, calcVolume, ceil10 } from "../utils";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Camera, Loader2, Upload, Plus, Minus, Sparkles, X, AlertTriangle, Tag } from "lucide-react";
import { toast } from "sonner";
import PackingSection from "./PackingSection";

const CONF = {
  high: { label: "Alta", cls: "bg-green-100 text-green-700", dot: "#22c55e", rank: 2 },
  medium: { label: "Media", cls: "bg-amber-100 text-amber-700", dot: "#f59e0b", rank: 1 },
  low: { label: "Bassa", cls: "bg-red-100 text-red-700", dot: "#ef4444", rank: 0 },
};

export default function ImageWorkArea() {
  const { addItemsBulk, setMode, settings, setAnalysisImageUrl, setImagePhotos, catalog } = useQuote();
  const pricePerM3 = settings?.pricePerM3 ?? 62.5;
  const [photos, setPhotos] = useState([]); // {id, dataUrl, url}
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [adding, setAdding] = useState(false);
  const [detected, setDetected] = useState(null); // array or null
  const fileRef = useRef();

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    if (photos.length + files.length > 15) { toast.error("Massimo 15 foto"); return; }
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name}: troppo grande (max 15MB)`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const p = { id: uid(), dataUrl: data.dataUrl, url: data.url };
        setPhotos((ps) => [...ps, p]);
        setAnalysisImageUrl(data.url);
      }
    } catch {
      toast.error("Errore durante il caricamento");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (id) => {
    setPhotos((ps) => ps.filter((p) => p.id !== id));
    setDetected(null);
  };

  const analyze = async () => {
    if (!photos.length) return;
    setAnalyzing(true);
    setProgress({ done: 0, total: photos.length });
    let counter = 0;
    const all = [];
    try {
      for (let i = 0; i < photos.length; i++) {
        const p = photos[i];
        try {
          const { data } = await api.post("/ai/analyze-photo", { imageUrl: p.dataUrl });
          (data.detectedItems || []).forEach((d) => {
            counter += 1;
            const l = Math.round(Number(d.length_cm) > 0 ? d.length_cm : 40);
            const w = Math.round(Number(d.width_cm) > 0 ? d.width_cm : 40);
            const h = Math.round(Number(d.height_cm) > 0 ? d.height_cm : 40);
            all.push({
              id: uid(), num: counter, photoId: p.id,
              label: d.label || "Oggetto",
              marker: d.marker || { x: 0.5, y: 0.5 },
              confidence: d.confidence || "medium",
              dims: { l, w, h },
              disassemblable: !!d.is_disassemblable,
              catalogMatch: d.catalogMatch || null,
              qty: 1, selected: true,
            });
          });
        } catch (e) {
          toast.error(`Errore analisi foto ${i + 1}`);
        }
        setProgress({ done: i + 1, total: photos.length });
      }
      setDetected(all);
      toast.success(`${all.length} oggetti rilevati su ${photos.length} foto`);
    } finally {
      setAnalyzing(false);
    }
  };

  const priceOf = (d) => {
    const unit = (d.fromCatalog && d.catalogPrice != null)
      ? ceil10(d.catalogPrice)
      : ceil10(calcVolume(d.dims.l, d.dims.w, d.dims.h) * pricePerM3);
    return unit * d.qty;
  };

  const findCat = (d) => {
    const key = (d.catalogMatch || "").trim().toLowerCase();
    if (!key) return null;
    return catalog.find((c) => (c.label || "").trim().toLowerCase() === key) || null;
  };

  const takeFromCatalog = (d) => {
    const c = findCat(d);
    if (!c) { toast.error("Oggetto non trovato a catalogo"); return; }
    updDetected(d.id, {
      label: c.label,
      dims: { l: c.length_cm, w: c.width_cm, h: c.height_cm },
      catalogPrice: c.price_eur,
      fromCatalog: true,
    });
    toast.success(`Dimensioni e prezzo da catalogo: ${c.label}`);
  };

  const applyAiEstimate = (d) => {
    updDetected(d.id, { fromCatalog: false, catalogPrice: null });
    toast.success("Ripristinata stima AI");
  };

  const add = async () => {
    const chosen = (detected || []).filter((d) => d.selected);
    if (!chosen.length) { toast.error("Seleziona almeno un oggetto"); return; }
    setAdding(true);
    try {
      const built = chosen.map((d) => {
        const base = {
          id: uid(), tipo: d.label, qty: d.qty, dims: { ...d.dims },
          note: "", fachMinutes: 0, smontMinutes: 0, imbMinutes: 0,
          includeSmont: d.disassemblable, includeImb: false,
          photoId: d.photoId, marker: d.marker, confidence: d.confidence,
        };
        if (d.fromCatalog && d.catalogPrice != null) {
          // prezzo e dimensioni presi dal catalogo
          return { ...base, pricePerMonth: ceil10(d.catalogPrice), fromCatalog: true, isPriceOverridden: false, isDimOverridden: false };
        }
        // prezzo dalle dimensioni stimate dall'AI (ignora catalogo)
        return { ...base, pricePerMonth: ceil10(calcVolume(d.dims.l, d.dims.w, d.dims.h) * pricePerM3), fromCatalog: false, isPriceOverridden: true, isDimOverridden: true };
      });
      setImagePhotos(photos.map((p) => ({ id: p.id, url: p.url, dataUrl: p.dataUrl })));
      addItemsBulk(built);
      setMode("image_done");
    } finally {
      setAdding(false);
    }
  };

  const toggle = (id) => setDetected((ds) => ds.map((d) => d.id === id ? { ...d, selected: !d.selected } : d));
  const updDetected = (id, patch) => setDetected((ds) => ds.map((d) => d.id === id ? { ...d, ...patch } : d));
  const updDims = (id, k, v) => setDetected((ds) => ds.map((d) => d.id === id ? { ...d, fromCatalog: false, catalogPrice: null, dims: { ...d.dims, [k]: Math.max(0, Math.round(Number(v) || 0)) } } : d));

  return (
    <div className="space-y-5 fade-in">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Camera className="h-5 w-5 text-[#1a5f5a]" />
            <h3 className="font-display font-semibold text-lg">Analisi foto</h3>
          </div>
          {photos.length > 0 && <span className="text-xs text-slate-400">{photos.length}/15 foto</span>}
        </div>

        <div onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
          className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors p-6 text-center cursor-pointer"
          data-testid="image-dropzone">
          {uploading ? <Loader2 className="h-7 w-7 animate-spin mx-auto text-[#1a5f5a]" /> : <Upload className="h-7 w-7 mx-auto text-slate-400" />}
          <p className="text-sm text-slate-500 mt-2">Trascina più foto qui o clicca per caricare (max 15 foto, 15MB ciascuna)</p>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="image-file-input" />
        </div>

        {photos.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {photos.map((p) => (
              <div key={p.id} className="relative rounded-md overflow-hidden border border-slate-200" data-testid={`photo-${p.id}`}>
                <img src={p.dataUrl} alt="Foto" className="w-full h-auto block bg-slate-50" />
                <button onClick={(e) => { e.stopPropagation(); removePhoto(p.id); }} className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1 hover:bg-black/80" data-testid={`photo-remove-${p.id}`}><X className="h-3.5 w-3.5" /></button>
                {(detected || []).filter((d) => d.photoId === p.id).map((d) => {
                  const conf = CONF[d.confidence] || CONF.medium;
                  return (
                    <button key={d.id} onClick={() => toggle(d.id)}
                      className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow transition-transform hover:scale-110"
                      title={`${d.label}`}
                      data-testid={`marker-${d.num}`}
                      style={{ left: `${d.marker.x * 100}%`, top: `${d.marker.y * 100}%`, width: 26, height: 26, background: conf.dot, opacity: d.selected ? 1 : 0.35 }}>
                      {d.num}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={analyze} disabled={analyzing} className="bg-[#1a5f5a] hover:bg-[#134744] gap-2" data-testid="image-analyze-button">
              {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {analyzing ? `Analisi ${progress.done}/${progress.total}…` : (detected ? "Analizza di nuovo" : "Analizza foto")}
            </Button>
            <Button variant="outline" onClick={() => { setPhotos([]); setDetected(null); }} data-testid="image-clear-button">Rimuovi tutte</Button>
          </div>
        )}
      </div>

      {detected && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <h3 className="font-display font-semibold text-lg mb-1">Oggetti rilevati</h3>
          <p className="text-xs text-slate-400 mb-3">Il prezzo è calcolato dalle dimensioni stimate dall'AI × tariffa €/m³. Puoi correggere nome, dimensioni, quantità e smontaggio.</p>
          {detected.filter((d) => d.confidence === "low").length > 0 && (
            <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3" data-testid="low-confidence-banner">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {detected.filter((d) => d.confidence === "low").length} oggetti a <b>bassa confidenza</b>: ricontrollali (nome e dimensioni) prima di inviare.
            </div>
          )}
          <div className="space-y-2">
            {[...detected].sort((a, b) => (CONF[a.confidence]?.rank ?? 1) - (CONF[b.confidence]?.rank ?? 1)).map((d) => {
              const conf = CONF[d.confidence] || CONF.medium;
              const isLow = d.confidence === "low";
              return (
                <div key={d.id} className={`border rounded-md p-3 ${isLow ? "border-red-300 bg-red-50/40" : "border-slate-200"}`} data-testid={`detected-item-${d.num}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: conf.dot }}>{d.num}</span>
                    <Switch checked={d.selected} onCheckedChange={() => toggle(d.id)} data-testid={`detected-toggle-${d.num}`} />
                    <Input value={d.label} onChange={(e) => updDetected(d.id, { label: e.target.value })} className="h-8 text-sm flex-1 min-w-0" data-testid={`detected-label-${d.num}`} />
                    <Badge className={`${conf.cls} text-[10px]`}>{conf.label}</Badge>
                    <div className="flex items-center gap-1.5">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updDetected(d.id, { qty: Math.max(1, d.qty - 1) })}><Minus className="h-3 w-3" /></Button>
                      <span className="w-5 text-center text-sm">{d.qty}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updDetected(d.id, { qty: d.qty + 1 })}><Plus className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2 pl-9">
                    <span className="text-[11px] text-slate-400">Dim (cm):</span>
                    {["l", "w", "h"].map((k) => (
                      <Input key={k} type="number" value={d.dims[k]} onChange={(e) => updDims(d.id, k, e.target.value)} className="h-7 w-16 text-sm" data-testid={`detected-dim-${k}-${d.num}`} />
                    ))}
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer ml-1">
                      <Switch checked={d.disassemblable} onCheckedChange={(v) => updDetected(d.id, { disassemblable: v })} data-testid={`detected-disasm-${d.num}`} />
                      Smontabile
                    </label>
                    {findCat(d) && (
                      d.fromCatalog ? (
                        <span className="flex items-center gap-1.5 ml-1">
                          <Badge className="bg-[#1a5f5a]/10 text-[#1a5f5a] text-[10px] gap-1"><Tag className="h-3 w-3" /> Da catalogo</Badge>
                          <button type="button" onClick={() => applyAiEstimate(d)} className="text-[10px] text-slate-400 hover:text-slate-600 underline" data-testid={`detected-use-ai-${d.num}`}>usa stima AI</button>
                        </span>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px] ml-1 border-[#1a5f5a]/40 text-[#1a5f5a] hover:bg-[#1a5f5a]/5" onClick={() => takeFromCatalog(d)} data-testid={`detected-take-catalog-${d.num}`}>
                          <Tag className="h-3 w-3" /> Prendi da catalogo
                        </Button>
                      )
                    )}
                    <span className="text-[11px] text-slate-500 ml-auto">≈ €{priceOf(d).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}/mese</span>
                  </div>
                </div>
              );
            })}
          </div>
          <Button onClick={add} disabled={adding} className="mt-4 bg-[#1a5f5a] hover:bg-[#134744] gap-2" data-testid="image-add-button">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Aggiungi selezionati
          </Button>
        </div>
      )}

      <PackingSection />
    </div>
  );
}
